import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { COMPANY, SERVICE_INTERESTS, CONTACT_IMAGE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-slate-900 border border-white/10 text-slate-100 placeholder:text-slate-600 rounded-none h-12 focus-visible:ring-1 focus-visible:ring-cyan-400 focus-visible:border-cyan-400 focus-visible:ring-offset-0";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service_interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_interest || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! Our team will get back to you within 1 business day.");
      setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? detail[0]?.msg || "Submission failed."
          : detail || "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const mapEmbed =
    "https://www.google.com/maps?q=Safal+Prelude+Corporate+Road+Prahlad+Nagar+Ahmedabad&output=embed";

  return (
    <main data-testid="contact-page" className="bg-slate-950 text-slate-100 min-h-screen">
      <PageHeader
        eyebrow="/ Contact"
        title={
          <>
            Let's design your
            <br />
            <span className="text-slate-500">security architecture.</span>
          </>
        }
        subtitle="Tell us about your facility, scale and timelines. A solutions consultant will reach out with an initial proposal within 1 business day."
        image={CONTACT_IMAGE}
      />

      <section className="py-20 md:py-24 px-6 md:px-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-px bg-white/5 border border-white/5">
            {/* Info */}
            <aside className="lg:col-span-4 bg-slate-950 p-8 md:p-10 space-y-7">
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="text-sm text-slate-100 hover:text-cyan-300 transition-colors block break-all"
                  >
                    {COMPANY.email}
                  </a>
                  <a
                    href={`mailto:${COMPANY.supportEmail}`}
                    className="text-xs text-slate-500 hover:text-cyan-300 transition-colors block break-all mt-1"
                  >
                    {COMPANY.supportEmail}
                  </a>
                </div>
              </div>

              <div className="border-t border-white/5 pt-7 flex items-start gap-3">
                <Phone className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    Phone
                  </div>
                  {COMPANY.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/\s+/g, "")}`}
                      className="text-sm text-slate-100 hover:text-cyan-300 transition-colors block"
                    >
                      {p}
                    </a>
                  ))}
                  <div className="text-xs text-slate-500 mt-1">
                    Landline: {COMPANY.landline}
                  </div>
                </div>
              </div>

              <div className="border-t border-white/5 pt-7 flex items-start gap-3">
                <MapPin className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    Office
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{COMPANY.address}</p>
                </div>
              </div>

              <div className="border-t border-white/5 pt-7 flex items-start gap-3">
                <Clock className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    Working Hours
                  </div>
                  <p className="text-sm text-slate-300">{COMPANY.hours}</p>
                </div>
              </div>

              <div className="border-t border-white/5 pt-7">
                <div className="flex items-center gap-3">
                  <img
                    src="/gem-new-logo-v6.svg"
                    alt="Gem logo"
                    className="h-8 w-auto object-contain"
                  />
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-mono">
                    {COMPANY.gem}
                  </div>
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="lg:col-span-8 bg-slate-950 p-8 md:p-10">
              <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-6">
                / Inquiry Form
              </div>
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your full name"
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@company.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+91 ..."
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                      Service Interest *
                    </Label>
                    <Select value={form.service_interest} onValueChange={(v) => update("service_interest", v)}>
                      <SelectTrigger data-testid="contact-service" className={`${inputCls} text-left`}>
                        <SelectValue placeholder="Select a solution" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10 text-slate-100 rounded-none">
                        {SERVICE_INTERESTS.map((s) => (
                          <SelectItem key={s} value={s} className="focus:bg-cyan-400/10 focus:text-cyan-200 rounded-none">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                    Project Brief *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your facility, expected scope and timelines."
                    rows={6}
                    className={`${inputCls} h-auto py-3 resize-none`}
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-slate-500">
                    By submitting you agree to be contacted by GenX Technologies.
                  </p>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-12 px-7 btn-glow disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        Send Inquiry <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 md:py-20 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-4">
            / Locate Us — Prahlad Nagar, Ahmedabad
          </div>
          <div
            className="border border-white/10 overflow-hidden h-[420px] relative"
            data-testid="contact-map"
          >
            <iframe
              title="GenX Technologies — Prahlad Nagar Office"
              src={mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
              style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.8)" }}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
